import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Camera, Shield, Home, Wifi, Server, CheckCircle } from "lucide-react";

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );
    const el = ref.current;
    if (el) {
      el.querySelectorAll(".animate-on-scroll").forEach((c) => observer.observe(c));
      if (el.classList.contains("animate-on-scroll")) observer.observe(el);
    }
    return () => observer.disconnect();
  }, []);
  return ref;
}

const projects = [
  {
    title: "Modern Smart Villa",
    location: "Kandy",
    category: "Smart Home + CCTV",
    icon: Home,
    desc: "Complete smart home automation with 16-camera CCTV system for a luxury villa.",
    tags: ["Smart Lighting", "CCTV", "Alarm System", "Voice Control"],
  },
  {
    title: "Corporate Office Complex",
    location: "Colombo",
    category: "Enterprise Security",
    icon: Server,
    desc: "Enterprise-grade surveillance and networking for a 5-floor office building.",
    tags: ["32 Camera System", "Enterprise WiFi", "Access Control", "Server Rack"],
  },
  {
    title: "Hotel Security Upgrade",
    location: "Nuwara Eliya",
    category: "CCTV + Networking",
    icon: Camera,
    desc: "Security system modernization with 64-camera installation and property-wide WiFi.",
    tags: ["64 Cameras", "Property WiFi", "Cloud Storage", "24/7 Monitoring"],
  },
  {
    title: "Residential Smart Home",
    location: "Peradeniya",
    category: "Smart Home",
    icon: Home,
    desc: "Full smart home automation including lighting, curtains, locks, and energy monitoring.",
    tags: ["Smart Lighting", "Smart Locks", "Smart Curtains", "Energy Monitor"],
  },
  {
    title: "Retail Store Security",
    location: "Kandy City",
    category: "CCTV + Alarm",
    icon: Shield,
    desc: "Comprehensive security solution for a retail chain with 3 locations.",
    tags: ["8 Cameras per Store", "Alarm System", "Remote Monitoring", "Mobile Alerts"],
  },
  {
    title: "Rural Connectivity",
    location: "Matale",
    category: "Starlink + Networking",
    icon: Wifi,
    desc: "Starlink internet installation and campus networking for a remote school.",
    tags: ["Starlink Setup", "Campus WiFi", "Structured Cabling", "Lab Network"],
  },
];

export default function Projects() {
  const ref = useInView();

  return (
    <div ref={ref}>
      {/* Hero */}
      <section className="pt-32 pb-20 section-padding bg-brand-gray">
        <div className="container-custom">
          <div className="max-w-3xl">
            <p className="animate-on-scroll text-sm font-semibold text-brand-orange uppercase tracking-wider mb-3">Our Work</p>
            <h1 className="animate-on-scroll stagger-1 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              Completed <span className="gradient-text">Projects</span>
            </h1>
            <p className="animate-on-scroll stagger-2 text-lg text-brand-muted leading-relaxed">
              Browse our portfolio of successful security and technology installations across Sri Lanka.
            </p>
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((p, i) => (
              <div key={i} className={`animate-on-scroll stagger-${(i % 4) + 1} bg-white rounded-3xl border border-brand-border/50 shadow-card hover:shadow-card-hover transition-all duration-500 hover:-translate-y-2 overflow-hidden group`}>
                <div className="bg-gradient-to-br from-brand-dark to-gray-800 h-48 flex items-center justify-center">
                  <div className="text-center">
                    <p.icon size={40} className="text-white/20 mx-auto mb-2" />
                    <p className="text-white/40 text-xs font-medium">{p.category}</p>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 text-sm text-brand-muted mb-2">
                    <MapPin size={14} />
                    <span>{p.location}</span>
                  </div>
                  <h3 className="font-display text-xl font-bold mb-2 group-hover:text-brand-orange transition-colors">{p.title}</h3>
                  <p className="text-sm text-brand-muted leading-relaxed mb-4">{p.desc}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {p.tags.map((t, j) => (
                      <span key={j} className="px-2.5 py-1 bg-brand-gray rounded-lg text-xs font-medium text-brand-dark/60">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="section-padding bg-brand-gray">
        <div className="container-custom">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { value: "500+", label: "Projects Done" },
              { value: "200+", label: "Happy Clients" },
              { value: "15+", label: "Districts Covered" },
              { value: "99%", label: "Client Satisfaction" },
            ].map((s, i) => (
              <div key={i} className={`animate-on-scroll stagger-${i + 1} text-center`}>                
                <p className="text-3xl sm:text-4xl font-bold text-brand-orange mb-1">{s.value}</p>
                <p className="text-sm text-brand-muted font-medium">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-brand-dark text-white text-center">
        <div className="container-custom">
          <h2 className="animate-on-scroll font-display text-3xl sm:text-4xl font-bold tracking-tight mb-6">
            Start Your Project
          </h2>
          <p className="animate-on-scroll stagger-1 text-lg text-white/60 mb-10 max-w-2xl mx-auto">
            Ready to transform your space? Let us design a custom solution for your home or business.
          </p>
          <div className="animate-on-scroll stagger-2">
            <Link to="/contact" className="btn-primary">
              Get Free Consultation <ArrowRight size={18} className="ml-2" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
